import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AvatarDisplay } from '@/components/AvatarDisplay';
import { Users } from 'lucide-react';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';
import { useNavigate } from 'react-router-dom';

interface PlayerFriendsSectionProps {
  userId: string;
  maxDisplay?: number;
}

export function PlayerFriendsSection({ userId, maxDisplay = 12 }: PlayerFriendsSectionProps) {
  const [friendIds, setFriendIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { profiles: friendProfiles, loading: profilesLoading } = usePlayerProfiles(friendIds);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFriends = async () => {
      setLoading(true);
      
      // Use the helper function we created
      const { data, error } = await supabase
        .rpc('get_user_friends', { target_user_id: userId });

      if (error) {
        console.error('Error fetching friends:', error);
      } else {
        const ids = (data || []).map((row: { friend_id: string }) => row.friend_id).slice(0, maxDisplay);
        setFriendIds(ids);
      }
      setLoading(false);
    };

    fetchFriends();
  }, [userId, maxDisplay]);

  if (loading || profilesLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Friends
          </CardTitle>
          <CardDescription>Loading friends...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (friendIds.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Friends
          </CardTitle>
          <CardDescription>No friends yet</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No friends to display</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const friendsList = friendIds.map(id => friendProfiles[id]).filter(Boolean);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Friends
          <span className="text-sm font-normal text-muted-foreground">({friendsList.length})</span>
        </CardTitle>
        <CardDescription>
          {friendsList.length === maxDisplay ? `Showing ${maxDisplay} friends` : `${friendsList.length} friends`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {friendsList.map((friend) => (
            <div
              key={friend.id}
              className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
              onClick={() => navigate(`/player/${friend.id}`)}
            >
              <AvatarDisplay
                username={friend.username}
                avatarConfig={friend.avatar_config}
                avatarUrl={friend.avatar_url}
                size="md"
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {friend.display_name || friend.username || 'Anonymous'}
                </p>
                {friend.username && (
                  <p className="text-xs text-muted-foreground truncate">
                    @{friend.username}
                  </p>
                )}
                <p className="text-xs font-semibold text-primary">
                  {friend.elo_rating}
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
